import { Link, useParams } from "react-router-dom";
import { useSearchContext } from "../context/SearchContext";
import { TripBreakdown } from "../components/TripBreakdown";
import { BookingButtons } from "../components/BookingButtons";
import { ShareCard } from "../components/ShareCard";
import type { Package, PackageTier } from "../types";
import {
  formatCents,
  formatDateLong,
  formatDuration,
  stopsLabel,
} from "../utils/format";

const TIER_LABEL: Record<PackageTier, string> = {
  cheapest: "Cheapest",
  best_value: "Best Value",
  premium: "Premium",
};

function PackageColumn({
  tier,
  pkg,
}: {
  tier: PackageTier;
  pkg: Package;
}): JSX.Element {
  return (
    <div className="flex flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-brand-700">
          {TIER_LABEL[tier]}
        </h3>
        <span className="text-2xl font-bold text-slate-900">
          {formatCents(pkg.total_cents)}
        </span>
      </div>

      <div className="mt-4">
        <TripBreakdown pkg={pkg} />
      </div>

      <dl className="mt-4 space-y-1 border-t border-slate-100 pt-4 text-xs text-slate-500">
        {pkg.flight_option && (
          <div className="flex justify-between">
            <dt>Flight</dt>
            <dd className="text-right text-slate-700">
              {pkg.flight_option.carrier} ·{" "}
              {stopsLabel(pkg.flight_option.stops)} ·{" "}
              {formatDuration(pkg.flight_option.total_duration_minutes)}
            </dd>
          </div>
        )}
        {pkg.hotel_option && (
          <div className="flex justify-between">
            <dt>Hotel</dt>
            <dd className="text-right text-slate-700">
              {pkg.hotel_option.name} · {pkg.hotel_option.star_rating}★
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
}

export function ShowDetailPage() {
  const { showId } = useParams();
  const { state } = useSearchContext();

  const show = state.results?.shows.find((s) => s.show_id === showId);
  const artistName = state.results?.artist.name ?? "";

  if (!show) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <h1 className="text-xl font-semibold text-slate-900">
          Show details unavailable
        </h1>
        <p className="mt-2 text-slate-500">
          We couldn&apos;t find this show in your current results. Run a search
          to see live pricing.
        </p>
        <Link
          to="/search"
          className="mt-6 inline-block rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700"
        >
          Back to search
        </Link>
      </div>
    );
  }

  const tiers: PackageTier[] = ["cheapest", "best_value", "premium"];

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <Link
        to="/search"
        className="text-sm text-brand-700 hover:underline"
      >
        ← Back to results
      </Link>

      <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {artistName} in {show.venue.city}, {show.venue.state}
          </h1>
          <p className="mt-1 text-slate-500">
            {formatDateLong(show.show_date)} · {show.venue.name}
            {show.venue.airport_distance_miles != null &&
              ` · ${show.venue.airport_distance_miles} mi from airport`}
          </p>
        </div>
        <BookingButtons showId={show.show_id} ticketUrl={show.ticket_url} />
      </div>

      <div className="mt-8 grid gap-5 md:grid-cols-3">
        {tiers.map((tier) => (
          <PackageColumn key={tier} tier={tier} pkg={show.packages[tier]} />
        ))}
      </div>

      <div className="mt-12 grid gap-8 md:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            Share this deal
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Post the all-in price to your story and tag your concert crew.
          </p>
          <div className="mt-4 max-w-xs">
            <ShareCard show={show} artistName={artistName} />
          </div>
        </div>

        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            What&apos;s included
          </h2>
          <ul className="mt-3 space-y-2 text-sm text-slate-600">
            <li>• Concert ticket to {artistName} at {show.venue.name}</li>
            <li>• Round-trip flight from your home airport</li>
            <li>• 2-night hotel stay near the venue</li>
            <li>• Prices refreshed from live partner inventory</li>
          </ul>
          <p className="mt-4 text-xs text-slate-400">
            Final prices are confirmed on the partner site at checkout.
          </p>
        </div>
      </div>
    </div>
  );
}
